import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { FaSync } from 'react-icons/fa';
import config from '../config';

const server_URL = config.SERVER_URL;
const EVENT_TYPES = ['list', 'tags', 'segments', 'automation'];

const EmailActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isSubuser, setIsSubuser] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setIsSubuser(userData.is_subuser || false);

    if (userData.role_id === 1) {
      fetchCustomers();
    }

    fetchLogs(userData.role_id, userData.id, '', '');
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    fetchLogs(userData.role_id, userData.id, customerFilter, eventTypeFilter);
  }, [customerFilter, eventTypeFilter]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchLogs = async (role, userId, custFilter, eventFilter) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/email_activity_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list',
          user_role: role,
          user_id: userId,
          is_subuser: userData.is_subuser || false,
          customer_id: role === 1 ? custFilter : undefined,
          event_type: eventFilter || undefined,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setLogs(data.data);
      } else {
        showToast(data.error || 'Failed to fetch email activity logs', 'error');
        setLogs([]);
      }
    } catch (error) {
      console.error('Error fetching email activity logs:', error);
      showToast('Error fetching email activity logs', 'error');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setCustomerFilter('');
    setEventTypeFilter('');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    fetchLogs(userData.role_id, userData.id, '', '');
  };

  const uniqueEventTypes = [...new Set(logs.map((log) => log.event_type))].filter(Boolean).sort();
  const eventTypeOptions = EVENT_TYPES.length ? EVENT_TYPES : uniqueEventTypes;

  return (
    <div style={{ padding: '20px' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Email Activity Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View latest 500 email-related activity logs with event type filters.
          </Typography>
        </Box>

        <Paper sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {userRole === 1 && !isSubuser && (
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel>Filter by Customer</InputLabel>
              <Select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                label="Filter by Customer"
                size="small"
              >
                <MenuItem value="">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Event Type</InputLabel>
            <Select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              label="Filter by Event Type"
              size="small"
            >
              <MenuItem value="">All Event Types</MenuItem>
              {(eventTypeOptions.length ? eventTypeOptions : uniqueEventTypes).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" color="warning" onClick={handleClear}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => fetchLogs(userRole, JSON.parse(localStorage.getItem('user') || '{}').id, customerFilter, eventTypeFilter)}>
              <FaSync className="me-2" />
              Refresh
            </Button>
          </Stack>
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {userRole === 1 && <TableCell><strong>Customer</strong></TableCell>}
                  <TableCell><strong>Event Type</strong></TableCell>
                  <TableCell><strong>Message</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={userRole === 1 ? 5 : 4} align="center" sx={{ py: 5 }}>
                      <Typography variant="h6" color="text.secondary">
                        No email activity logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((row) => (
                    <TableRow key={row.id} hover>
                      {userRole === 1 && (
                        <TableCell>
                          <Typography variant="body2">{row.customer_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customer_email || 'N/A'}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip label={row.event_type || 'N/A'} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.message}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.md5_email || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmailActivityLogs;

